import type {CouponForm, CouponTemplate} from './types';

import {Button} from '@ds/components';
import {cn} from '@ds/helpers';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store';
import {createCouponPreorder} from 'store/slices/coupon/thunk';

import {getCouponVendorId} from '../utils';
import CouponDateRange from './components/CouponDateRange/CouponDateRange';
import CouponOtpSheet from './components/CouponOtpSheet/CouponOtpSheet';
import CouponReview from './components/CouponReview/CouponReview';
import CouponTemplateSelect from './components/CouponTemplateSelect/CouponTemplateSelect';

const CouponAdd: React.FC = () => {
  const dispatch = useAppDispatch();
  const vendorId = getCouponVendorId();
  const {create} = useAppSelector((state) => state.coupon);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<CouponForm>({date: {}});
  const [isOtpOpen, setIsOtpOpen] = useState(false);

  const selectCoupon = (coupon: CouponTemplate) => {
    setForm((current) => ({...current, coupon}));
    setStep(2);
  };

  const createPreorder = async (): Promise<boolean> => {
    if (!form.coupon || !form.date.start || !form.date.end) {
      toast('اطلاعات کوپن کامل نیست.');
      return false;
    }

    try {
      await dispatch(
        createCouponPreorder({
          vendor_id: vendorId,
          start_time: new Date(form.date.start),
          end_time: new Date(form.date.end),
          template_id: form.coupon.id
        })
      ).unwrap();
      return true;
    } catch {
      toast('مشکلی پیش آمده. دوباره تلاش کنید!');
      return false;
    }
  };

  const submitForm = async () => {
    const wasCreated = await createPreorder();
    if (wasCreated) {
      setIsOtpOpen(true);
    }
  };

  return (
    <PageLayout
      footerPosition="static"
      contentClassName="overflow-hidden"
      header={
        <div className="flex h-3 gap-1 px-4 py-1" aria-label={`مرحله ${step} از ۳`}>
          {[1, 2, 3].map((item) => (
            <span
              key={item}
              className={cn('h-1 flex-1 bg-gray-24', item <= step && 'bg-orange-flame')}
              aria-hidden
            />
          ))}
        </div>
      }
      footer={
        step > 1 ? (
          <div className="flex justify-between gap-3">
            <Button variant="ghost" color="secondary" onClick={() => setStep(step - 1)}>
              مرحله قبل
            </Button>
            {step === 2 ? (
              <Button disabled={!form.date.start || !form.date.end} onClick={() => setStep(3)}>
                مرحله بعد
              </Button>
            ) : (
              <Button loading={create.loading} onClick={() => void submitForm()}>
                تأیید و ایجاد کوپن
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      {step === 1 ? <CouponTemplateSelect vendorId={vendorId} onSelect={selectCoupon} /> : null}
      {step === 2 ? (
        <CouponDateRange
          value={form.date}
          onChange={(date) => setForm((current) => ({...current, date}))}
        />
      ) : null}
      {step === 3 ? <CouponReview form={form} onEditDate={() => setStep(2)} /> : null}
      <CouponOtpSheet
        vendorId={vendorId}
        show={isOtpOpen}
        onClose={() => setIsOtpOpen(false)}
        onResend={createPreorder}
      />
    </PageLayout>
  );
};

export default CouponAdd;
